/* @flow */
import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';
import { useHref } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { selectFeatureFlag } from '@gandi/authentication';
import type { Currency } from '@gandi/billing';
import type { NotifyAction } from '@gandi/notifications';
import * as coupons from '@gandi/redux-resource-coupon';
import { instances as instancesPrice } from '@gandi/redux-resource-price';
import type { InstancePrice } from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';

import type { TranslatorProps } from '@gandi/translate';
import { Button, Spinner, Base, Alert } from '@gandi/ui';

import type { FormComponentProps } from 'gandi.react-form';

import { Intro } from '../../../Layout';
import setPublicHTTPCache from '../../../modules/app/public-http-cache';
import type {
  FullState,
  // $FlowFixMe[missing-export]
  GandiLogger,
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
} from '../../../types';
import { fetchInstancePrices } from '../../modules/offers';
import type { InstanceType } from '../../modules/types';

import SimpleHostingCreateForm from './Form';

import styles from './SimpleHostingCreate.scss';

export type Configuration = {
  all: Array<{ label: string, name: string, value: ?InstanceType, ... }>,
  selected: ?string,
};

export type PresenterStateProps = {
  backUrl: string,
  cartExists: boolean,
  cartIsValid: boolean,
  currency: Currency,
  country: string,
  // Required data status
  isFetching: boolean,
  isLoaded: boolean,
  isFailure: boolean,
  formName: string,
  formValues: { +[field: string]: mixed, ... },
  initialValues: { +[field: string]: mixed, ... },
  instancePrices: {
    // All possible instance prices
    all: Array<InstancePrice>,
    // available instance prices
    available: Array<InstancePrice>,
    // features shared by every prices
    sharedFeatures: InstancePrice['features'],
    // the trial offer if available
    trial: ?InstancePrice,
    ...
  },
  recommendedFeatures: Array<InstanceType>,
  overviewUrl: string,
  taxesIncluded: boolean,
};

export type PresenterDispatchProps = {
  fetchRequiredData: (
    discountCode?: string,
  ) => Promise<Array<ReturnType<<T>(p: Promise<T> | T) => T>>>,
  notifyDanger: NotifyAction,
};

type Params = { cartId?: string, productId?: string };

type Query = {
  discount_code?: string,
  cn?: string,
  configuration?: string,
  reason?: string,
};

export type PresenterOwnProps = {
  form: Partial<FormComponentProps>,
  logger: GandiLogger,
  translator: TranslatorProps,
  ...WithRouterProps<Params, Query>,
};

export type PresenterProps = {
  simplehostingURL: string,
  ...PresenterStateProps,
  ...PresenterDispatchProps,
  ...PresenterOwnProps,
};

/**
 * SimpleHostingCreatePresenter component
 */
class SimpleHostingCreatePresenter extends React.Component<PresenterProps> {
  static preFetch({
    dispatch,
    getState,
    query,
  }: PreFetchArgs<
    {},
    Params,
    Query,
    FullState,
  >): PreFetchReturn<void | Promise<any>> {
    const state = getState();
    const sharingId = selectCurrentId(state);
    const discountCode = query.discount_code;

    const options = {};

    if (discountCode) {
      // $FlowIgnore[prop-missing]
      options.discounts = [discountCode];
    }

    return Promise.allSettled([
      !instancesPrice.selectIsFailure(state) &&
        !instancesPrice.selectIsFetching(state) &&
        dispatch(fetchInstancePrices(options)),
      sharingId &&
        // $FlowFixMe[prop-missing]
        !coupons.selectIsFailure(state) &&
        // $FlowFixMe[prop-missing]
        !coupons.selectIsFetching(state) &&
        dispatch(coupons.fetchAll({ sharingId })),
    ]).catch(() => {
      // Clean-up server-side state in case of error.
      // This is required to prevent transmission of loading status
      // for other resources.
      if (global.__SERVER__) {
        if (instancesPrice.selectIsFetching(getState())) {
          dispatch(instancesPrice.abort());
        }
      }
    });
  }

  static handleRedirect(
    args: StaticHandlerArgs<{}, Params, empty, FullState>,
  ): Promise<any> {
    const FF_SHS_LEGACY_CREATION = selectFeatureFlag(args.getState(), {
      featureFlagName: 'ff-shs-legacy-creation',
    });

    if (!FF_SHS_LEGACY_CREATION) {
      args.redirect('/simplehosting/create');
      throw new Error('Redirecting to Web Hosting creation.');
    }

    // $FlowFixMe[incompatible-exact]: don't know how to inherite correctly
    // $FlowFixMe[prop-missing]
    setPublicHTTPCache(args);
    return Promise.resolve();
  }

  componentDidMount() {
    const query = this.props.location.query;
    this.props.fetchRequiredData(query.discount_code);
  }

  decorateContent(...content: Array<React.Node>): React.MixedElement {
    return <div>{React.Children.toArray(content.filter(Boolean))}</div>;
  }

  renderButtons(): React.MixedElement {
    const { __ } = this.props.translator;

    return (
      <p className={styles.buttonContainer}>
        <Button
          href={this.props.form.submitting ? undefined : this.props.backUrl}
          rel="external"
          kind="outline"
          className={styles.button}
          disabled={this.props.form.submitting}
        >
          {__('Back')}
        </Button>
        <Button
          type="submit"
          form={this.props.formName}
          className={styles.button}
          disabled={!this.props.form.valid || this.props.form.submitting}
        >
          {__('Add to cart')}
        </Button>
      </p>
    );
  }

  /*
   * FIXME: This is not completely true.
   * A Cart that has already been committed but not yet paid
   * will have a `cart.is_reopenable` set to `true`.
   * In this case, we could suggest the user to reopen the cart, or do it for him silently.
   * Also, at the moment, user is redirected to the overview by Root component when this happens
   * See /src/components/Root.js#L44-48
   */
  renderInvalidCartError(): React.Node {
    const { __ } = this.props.translator;

    const message = __(
      '<p>' +
        'Your shopping cart appears to be invalid. ' +
        'It has either been closed or paid already.' +
        '</p>' +
        '<p>' +
        'Therefore, no further products can be added to this cart. ' +
        '</p>' +
        '<p>' +
        'We suggest you <a href="%(href)s">continue with a new shopping cart</a>.' +
        '</p>',
      {
        withHTML: true,
        href: this.props.simplehostingURL,
      },
    );

    /* eslint-disable react/no-danger */
    return (
      <Base marginBottom="base">
        <Alert
          message={<div dangerouslySetInnerHTML={message} />}
          intent="danger"
        />
      </Base>
    );
    /* eslint-enable react/no-danger */
  }

  renderError(): React.Node {
    const { __ } = this.props.translator;

    const styleJsHidden = global.__SERVER__ ? styles['js-hidden'] : '';

    return (
      <Base marginBottom="base" className={styleJsHidden}>
        <Alert
          intent="danger"
          message={__(
            'An unexpected error occurred. ' +
              'Please refresh the page or try again later..',
          )}
          buttons={
            <Button
              type="button"
              small
              onClick={this.props.fetchRequiredData}
              disabled={this.props.isFetching}
            >
              {__('Retry')}
            </Button>
          }
        />
      </Base>
    );
  }

  renderForm(): React.Node {
    return <SimpleHostingCreateForm {...this.props} />;
  }

  renderIntro(): React.Node {
    return (
      <Intro
        icon="simplehosting"
        title={this.props.translator.__('Create a new instance')}
      />
    );
  }

  /**
   * Render component
   * @return {ReactElement}
   */
  render(): React.MixedElement {
    if (!this.props.cartIsValid) {
      return this.decorateContent(this.renderInvalidCartError());
    }

    if (this.props.isFailure && !this.props.isFetching) {
      return this.decorateContent(this.renderIntro(), this.renderError());
    }

    if (
      !this.props.isLoaded ||
      (!this.props.isLoaded && this.props.isFetching) ||
      this.props.form.submitting
    ) {
      return this.decorateContent(this.renderIntro(), <Spinner centered />);
    }

    return this.decorateContent(
      this.renderIntro(),
      this.renderForm(),
      this.renderButtons(),
    );
  }
}

function withSimpleHostingURL(Component: React.ComponentType<PresenterProps>) {
  const Wrapper = (props: PresenterProps) => {
    const simplehostingURL = useHref('/simplehosting/create');
    return <Component simplehostingURL={simplehostingURL} {...props} />;
  };

  return hoistNonReactStatics(Wrapper, Component);
}

export default (withSimpleHostingURL(
  SimpleHostingCreatePresenter,
): React.ComponentType<PresenterProps>);
